// Doggy Jump – Time Rush v4.0.2  –  ES Module for React
// Migrated from standalone game.js — Sprint B
// Exports: initGame, destroyGame, startGame, restartGame, downloadScreenshot, shareOnTwitter, keys

// EMBED sprites are lazy-loaded to keep the main bundle small (~617KB of base64)
let EMBED = {};

// ─── Asset paths (served from public/minigame/) ────────────────────────────
const ASSET_BASE = process.env.PUBLIC_URL + '/minigame/';
const ASSET = {
  idle:     ASSET_BASE + 'assets/idle.png',
  jump:     ASSET_BASE + 'assets/jump.png',
  fall:     ASSET_BASE + 'assets/fall.png',
  bg:       ASSET_BASE + 'assets/bg.png',
  platform: ASSET_BASE + 'assets/platform.png',
  badge:    ASSET_BASE + 'assets/badge.png',
  music:    ASSET_BASE + 'musicback.mp3',
  sfx: {
    start: ASSET_BASE + 'assets/sfx/start.wav',
    jump:  ASSET_BASE + 'assets/sfx/jump.wav',
    coin:  ASSET_BASE + 'assets/sfx/coin.wav',
    fall:  ASSET_BASE + 'assets/sfx/fall.wav',
    over:  ASSET_BASE + 'assets/sfx/over.wav',
    tick:  ASSET_BASE + 'assets/sfx/tick.wav',
  }
};

// ─── Physics & difficulty constants ─────────────────────────────────────────
const W = 480, H = 720;
const GRAVITY = 0.5, MOVE_SPEED = 4.2, JUMP_V = -12.0;
const timeUpFrames = Math.abs(JUMP_V) / GRAVITY;
const airFrames = Math.floor(timeUpFrames * 2);
const maxVerticalReach = (JUMP_V * JUMP_V) / (2 * GRAVITY);
const maxHorizontalReach = MOVE_SPEED * airFrames;
const VERT_CAP = Math.floor(maxVerticalReach - 16);
const HORZ_CAP = Math.floor(maxHorizontalReach - 16);
const DIFF_MULT = 1.5;
const MIN_VERTICAL_GAP = Math.round(70 * DIFF_MULT);
const MIN_PLATFORM_W = 48;
const MAX_PLATFORM_W = 160;
const SHRINK_PER_ROW = 1.0 * DIFF_MULT;
const GAME_DURATION = 30000;

// ─── Anti-Cheat: Safe Time Reference ────────────────────────────────────────
// Guardamos una referencia inmutable a performance.now para evitar que
// un usuario malicioso la sobrescriba en la consola (Speedhack / Time Dilation)
const safeNow = typeof performance !== 'undefined' && performance.now 
  ? performance.now.bind(performance) 
  : Date.now.bind(Date);

// ─── Shared input state (React touch controls write here) ───────────────────
export const keys = { left: false, right: false, jump: false, jumpPressed: false, restart: false };

// ─── Module-level state ─────────────────────────────────────────────────────
let canvas, ctx;
let callbacks = {};  // { onScore, onTimer, onGameOver, onGameStart }
let animFrameId = null;
let keydownHandler = null, keyupHandler = null;
let audioCtx = null, audioUnlocked = false;
let bgMusic = null, musicEnabled = true;
let firstInteractionHandlers = [];

// Sprites
let playerIdle, playerJump, playerFall, bgImg, platformImg, badgeImg;

// SFX — Web Audio API (reliable on Android)
let SFX = {};
let audioBufferCache = {};  // key → { raw: ArrayBuffer|null, decoded: AudioBuffer|null }
let sfxVolumes = {};        // key → volume level

// ─── Pre-fetch audio file as ArrayBuffer (non-blocking) ────────────────────
async function prefetchAudio(key, url, vol) {
  sfxVolumes[key] = vol;
  try {
    const resp = await fetch(url);
    audioBufferCache[key] = { raw: await resp.arrayBuffer(), decoded: null };
  } catch (e) {
    console.warn(`[DoggyJump] prefetch ${key}:`, e);
  }
}

// ─── Decode pre-fetched ArrayBuffers into AudioBuffers ─────────────────────
async function decodeBuffers() {
  if (!audioCtx) return;
  for (const key of Object.keys(audioBufferCache)) {
    const entry = audioBufferCache[key];
    if (entry.decoded || !entry.raw) continue;
    try {
      entry.decoded = await audioCtx.decodeAudioData(entry.raw.slice(0));
    } catch (e) {
      console.warn(`[DoggyJump] decode ${key}:`, e);
    }
  }
}

// ─── Play a decoded SFX buffer via Web Audio API ───────────────────────────
function playSfx(key) {
  if (!audioCtx || audioCtx.state !== 'running') return;
  const entry = audioBufferCache[key];
  if (!entry?.decoded) return;
  try {
    const source = audioCtx.createBufferSource();
    const gain = audioCtx.createGain();
    gain.gain.value = sfxVolumes[key] ?? 0.7;
    source.buffer = entry.decoded;
    source.connect(gain);
    gain.connect(audioCtx.destination);
    source.start(0);
  } catch (e) { /* ignore */ }
}

// ─── Image loader with fallback ─────────────────────────────────────────────
function loadWithFallback(path, dataURI, label) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => console.log(`[DoggyJump] ${label} OK desde`, path);
  img.onerror = () => {
    console.warn(`[DoggyJump] ${label} falló desde`, path, '→ usando embebido');
    img.src = dataURI;
  };
  img.src = path + '?v=' + Date.now();
  return img;
}

// ─── AudioContext unlock + decode SFX buffers ─────────────────────────────
async function ensureAudio() {
  try {
    const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
    if (!audioCtx) audioCtx = new AudioCtxClass();
    if (audioCtx.state === 'suspended') await audioCtx.resume();
    if (!audioUnlocked) {
      const b = audioCtx.createBuffer(1, 1, 22050);
      const s = audioCtx.createBufferSource();
      s.buffer = b;
      s.connect(audioCtx.destination);
      s.start(0);
      audioUnlocked = true;
    }
    // Decode any pre-fetched SFX buffers that haven't been decoded yet
    await decodeBuffers();
  } catch (e) { /* silencioso */ }
}

// ─── Classes ────────────────────────────────────────────────────────────────
class Player {
  constructor(x, y) {
    this.x = x; this.y = y; this.prevY = y;
    this.w = 40; this.h = 40;
    this.vx = 0; this.vy = 0;
    this.onGround = true; this.points = 0;
  }
  update(started) {
    if (!started) { this.prevY = this.y; this.vx = 0; this.vy = 0; return; }
    this.prevY = this.y;
    let target = 0;
    if (keys.left) target -= MOVE_SPEED;
    if (keys.right) target += MOVE_SPEED;
    this.vx += (target - this.vx) * 0.35;
    this.x += this.vx;
    if (this.x < -this.w) this.x = W;
    if (this.x > W) this.x = -this.w;
    if (keys.jumpPressed && this.onGround) {
      this.vy = JUMP_V; this.onGround = false;
      game.jumpCount += 1;
      SFX.jump();
    }
    keys.jumpPressed = false;
    this.vy += GRAVITY;
    this.y += this.vy;
  }
  draw() {
    let img = playerIdle;
    if (!this.onGround) {
      if (this.vy < -0.5) img = playerJump;
      else if (this.vy > 0.5) img = playerFall;
    }
    if (img.complete && img.naturalWidth > 0)
      ctx.drawImage(img, this.x, this.y, this.w, this.h);
    else {
      ctx.fillStyle = '#fff';
      ctx.fillRect(this.x, this.y, this.w, this.h);
    }
  }
}

class Platform {
  constructor(x, y, w, isFloor = false) {
    this.x = x; this.y = y; this.w = w; this.h = 12;
    this.stepped = false; this.isFloor = isFloor;
    if (isFloor) this.stepped = true;
    this.vx = isFloor ? 0 : ((Math.random() < 0.5 ? -1 : 1) * (0.35 + Math.random() * 0.5));
  }
  draw() {
    if (platformImg.complete && platformImg.naturalWidth > 0) {
      ctx.drawImage(platformImg, this.x, this.y, this.w, this.h);
    } else {
      ctx.fillStyle = '#8ec5ff';
      ctx.fillRect(this.x, this.y, this.w, this.h);
      ctx.strokeStyle = '#2a5a9a';
      ctx.strokeRect(this.x + 0.5, this.y + 0.5, this.w - 1, this.h - 1);
    }
  }
}

class Coin {
  constructor(x, y) { this.x = x; this.y = y; this.r = 7; this.collected = false; }
  draw() {
    if (this.collected) return;
    ctx.beginPath(); ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
    ctx.fillStyle = '#ffd34e'; ctx.fill();
    ctx.strokeStyle = '#a67c00'; ctx.stroke();
    ctx.beginPath(); ctx.arc(this.x - 3, this.y - 3, this.r * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,.55)'; ctx.fill();
  }
}

function circleRectCollide(cx, cy, r, rx, ry, rw, rh) {
  const closestX = Math.max(rx, Math.min(cx, rx + rw));
  const closestY = Math.max(ry, Math.min(cy, ry + rh));
  const dx = cx - closestX, dy = cy - closestY;
  return (dx * dx + dy * dy) <= r * r;
}

// ─── Floating score popups ──────────────────────────────────────────────────
let floatingTexts = [];

function addFloatingText(x, y, text, color = '#4caf50') {
  floatingTexts.push({ x, y, text, color, opacity: 1.0, age: 0 });
}

function updateFloatingTexts() {
  for (const ft of floatingTexts) {
    ft.y -= 1.5;
    ft.age++;
    ft.opacity = Math.max(0, 1 - ft.age / 40);
  }
  floatingTexts = floatingTexts.filter(ft => ft.opacity > 0);
}

function drawFloatingTexts() {
  for (const ft of floatingTexts) {
    ctx.save();
    ctx.globalAlpha = ft.opacity;
    ctx.font = 'bold 22px Nunito, sans-serif';
    ctx.fillStyle = ft.color;
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 3;
    ctx.textAlign = 'center';
    ctx.strokeText(ft.text, ft.x, ft.y);
    ctx.fillText(ft.text, ft.x, ft.y);
    ctx.restore();
  }
}

// ─── Game object ────────────────────────────────────────────────────────────
const game = {
  player: null, platforms: [], coins: [],
  scrollY: 0, difficultyShrink: 0,
  over: false, started: false,
  startTime: 0, remaining: GAME_DURATION, lastSecond: 31,
  spawnedCount: 0,
  jumpCount: 0,
  coinCount: 0,
  platformPointCount: 0,

  reset() {
    this.player = new Player(W / 2 - 20, H - 140);
    this.platforms = []; this.coins = []; this.scrollY = 0; this.difficultyShrink = 0;
    this.over = false; this.started = false; this.spawnedCount = 0;
    this.jumpCount = 0;
    this.coinCount = 0;
    this.platformPointCount = 0;
    floatingTexts = [];

    // Notify React: state → idle
    if (callbacks.onStateChange) callbacks.onStateChange('idle');

    const floorY = H - 60;
    const floor = new Platform(W / 2 - 120, floorY, 240, true);
    this.platforms.push(floor);

    let y = floorY - 100, w = 120, prev = floor;
    for (let i = 0; i < 10; i++) {
      const p = this.spawnOnePlatform(y, w, prev);
      prev = p;
      if (Math.random() < 0.55) this.coins.push(new Coin(p.x + p.w / 2, p.y - 18));
      const gap = MIN_VERTICAL_GAP + Math.random() * (this.currentVertCap() - MIN_VERTICAL_GAP);
      y -= gap;
      w = Math.max(MIN_PLATFORM_W, w - (SHRINK_PER_ROW + Math.random() * 9));
    }
    this.remaining = GAME_DURATION;
    this.lastSecond = 31;
    this.updateUI();
  },

  start() {
    if (this.started) return;
    this.started = true;
    this.startTime = safeNow();
    // Unlock AudioContext + decode buffers, then play start SFX (user gesture)
    ensureAudio().then(() => {
      SFX.start();
    });
    // Notify React: state → playing
    if (callbacks.onStateChange) callbacks.onStateChange('playing');
    if (callbacks.onGameStart) callbacks.onGameStart();
    tryPlayMusic();
  },

  updateUI() {
    if (!this.player) return;
    const pts = this.player.points;
    const s = Math.max(0, Math.ceil(this.remaining / 1000));
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    const timeStr = `${mm}:${ss}`;
    if (callbacks.onScore) callbacks.onScore(pts);
    if (callbacks.onTimer) callbacks.onTimer(timeStr);
  },

  currentVertCap() {
    const diff = Math.min(1, (this.spawnedCount / 40));
    const baseMax = Math.min(VERT_CAP, 110 + 50 * diff);
    return Math.min(baseMax, VERT_CAP);
  },
  currentHorzCap() {
    const diff = Math.min(1, (this.spawnedCount / 40));
    const baseMax = Math.min(HORZ_CAP, 120 + 80 * diff);
    return Math.min(baseMax, HORZ_CAP);
  },

  spawnOnePlatform(y, w, prev) {
    const prevCenter = prev.x + prev.w / 2;
    const horzCap = this.currentHorzCap();
    const minX = Math.max(8, prevCenter - horzCap - w / 2);
    const maxX = Math.min(W - w - 8, prevCenter + horzCap - w / 2);
    const x = minX + Math.random() * Math.max(0, (maxX - minX));
    const p = new Platform(x, y, w);
    this.platforms.push(p);
    this.spawnedCount++;
    return p;
  },

  spawnPlatformRow(topY) {
    const baseW = Math.max(MIN_PLATFORM_W, MAX_PLATFORM_W - this.difficultyShrink);
    const w = Math.max(MIN_PLATFORM_W, baseW - Math.random() * 50);
    let prev = this.platforms.reduce((acc, p) => p.y < acc.y ? p : acc, this.platforms[0]);
    const p = this.spawnOnePlatform(topY, w, prev);
    if (Math.random() < 0.55) this.coins.push(new Coin(p.x + p.w / 2, p.y - 18));
    this.difficultyShrink = Math.min((MAX_PLATFORM_W - MIN_PLATFORM_W), this.difficultyShrink + SHRINK_PER_ROW);
  },

  handleCollisions() {
    const pl = this.player;
    if (pl.vy >= 0) {
      const prevBottom = pl.prevY + pl.h;
      const currBottom = pl.y + pl.h;
      for (const p of this.platforms) {
        const overlapX = pl.x + pl.w > p.x && pl.x < p.x + p.w;
        const crossedTop = prevBottom <= p.y && currBottom >= p.y;
        if (overlapX && crossedTop) {
          const centerX = pl.x + pl.w / 2;
          if (centerX > p.x + 5 && centerX < p.x + p.w - 5) {
            pl.y = p.y - pl.h; pl.vy = 0; pl.onGround = true;
            if (!p.stepped) {
              pl.points += 10;
              p.stepped = true;
              this.platformPointCount += 1;
              addFloatingText(pl.x + pl.w / 2, pl.y - 8, '+10', '#F5C542');
            }
            return;
          }
        }
      }
    }
    pl.onGround = false;
    for (const c of this.coins) {
      if (c.collected) continue;
      if (circleRectCollide(c.x, c.y, c.r, pl.x, pl.y, pl.w, pl.h)) {
        c.collected = true; pl.points += 5;
        this.coinCount += 1;
        addFloatingText(c.x, c.y - 12, '+5', '#4caf50');
        SFX.coin();
      }
    }
  },

  step(dt, now) {
    if (this.over) return;
    if (this.started) {
      this.remaining = Math.max(0, GAME_DURATION - (now - this.startTime));
      const sec = Math.ceil(this.remaining / 1000);
      if (sec <= 3 && sec < this.lastSecond) { SFX.tick(); }
      this.lastSecond = sec;
      if (this.remaining <= 0) return this.end('time');
    }

    this.player.update(this.started);

    if (this.started) {
      const targetY = H * 0.4;
      if (this.player.y < targetY) {
        const diff = targetY - this.player.y;
        this.player.y += diff; this.scrollY += diff;
        for (const p of this.platforms) p.y += diff;
        for (const c of this.coins) c.y += diff;
        let topY = this.platforms.reduce((m, p) => Math.min(m, p.y), Infinity);
        while (topY > -40) {
          const gap = MIN_VERTICAL_GAP + Math.random() * (this.currentVertCap() - MIN_VERTICAL_GAP);
          topY -= gap;
          this.spawnPlatformRow(topY);
        }
      }
    }

    // Movimiento lateral y rebote
    for (const p of this.platforms) {
      if (!p.isFloor) {
        p.x += p.vx;
        if (p.x < 8) { p.x = 8; p.vx *= -1; }
        if (p.x + p.w > W - 8) { p.x = W - 8 - p.w; p.vx *= -1; }
      }
    }

    // Limpieza
    this.platforms = this.platforms.filter(p => p.y < H + 80);
    this.coins = this.coins.filter(c => !c.collected && c.y < H + 40);

    // Colisiones y caída
    this.handleCollisions();
    updateFloatingTexts();
    if (this.started && this.player.y > H) {
      return this.end('fall');
    }
    if (!this.started && this.player.y > H + 40) {
      const floor = this.platforms.find(p => p.isFloor) || { y: H - 60 };
      this.player.y = floor.y - this.player.h;
      this.player.vx = 0; this.player.vy = 0; this.player.onGround = true;
    }

    if (keys.restart) { keys.restart = false; this.reset(); }
    this.updateUI();
  },

  end(reason) {
    this.over = true;
    const pts = this.player.points;
    const elapsedMs = this.started
      ? Math.max(0, Math.min(GAME_DURATION, Math.round(safeNow() - this.startTime)))
      : 0;
    const runMeta = {
      durationMs: elapsedMs,
      endedBy: reason,
      jumps: this.jumpCount,
      coins: this.coinCount,
      platformBonuses: this.platformPointCount,
      scoreFromMeta: this.coinCount * 5 + this.platformPointCount * 10,
    };
    if (reason === 'fall') { SFX.fall(); } else { SFX.over(); }
    // Notify React: state → over
    if (callbacks.onStateChange) callbacks.onStateChange('over');
    if (callbacks.onGameOver) callbacks.onGameOver(reason, pts, runMeta);
  },

  drawBackground() {
    if (bgImg.complete && bgImg.naturalWidth > 0) {
      ctx.drawImage(bgImg, 0, 0, W, H);
    } else {
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#dff4ff'); g.addColorStop(1, '#a9d6ff');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    }
  },

  render() {
    ctx.clearRect(0, 0, W, H);
    this.drawBackground();
    for (const p of this.platforms) p.draw();
    for (const c of this.coins) c.draw();
    this.player.draw();
    drawFloatingTexts();
  }
};

// ─── Game loop ──────────────────────────────────────────────────────────────
// Fixed-timestep loop: physics always run at ~60fps regardless of display
// refresh rate (120Hz screens, ProMotion, etc. would otherwise double speed).
const MS_PER_FRAME = 1000 / 60; // 16.667ms

let lastTs = 0;
function loop(ts) {
  animFrameId = requestAnimationFrame(loop);

  // First frame — just record timestamp, don't step
  if (lastTs === 0) {
    lastTs = ts;
    return;
  }

  const elapsed = ts - lastTs;

  // Too soon: skip physics this frame (throttle 120Hz → 60fps)
  if (elapsed < MS_PER_FRAME - 1) return;

  // Prevent spiral-of-death after tab wake-up or long pause
  lastTs = elapsed > MS_PER_FRAME * 3 ? ts : lastTs + MS_PER_FRAME;

  game.step(1, ts);
  game.render();
}

// ─── Background music ───────────────────────────────────────────────────────
function tryPlayMusic() {
  if (!musicEnabled) return;
  // Create bgMusic lazily on user gesture (Android requires this)
  if (!bgMusic) {
    try {
      bgMusic = new Audio(ASSET.music);
      bgMusic.loop = true;
      bgMusic.volume = 0.5;
    } catch (e) { return; }
  }
  const p = bgMusic.play();
  if (p && typeof p.then === 'function') {
    p.catch(() => { /* autoplay blocked */ });
  }
}

// ─── Score card (for download) ──────────────────────────────────────────────
function roundRect(c, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  c.moveTo(x + rr, y);
  c.arcTo(x + w, y, x + w, y + h, rr);
  c.arcTo(x + w, y + h, x, y + h, rr);
  c.arcTo(x, y + h, x, y, rr);
  c.arcTo(x, y, x + w, y, rr);
  c.closePath();
}

function drawScoreCard(c, w, h, points) {
  const grad = c.createRadialGradient(w / 2, h / 2, 10, w / 2, h / 2, Math.max(w, h) / 1.2);
  grad.addColorStop(0, 'rgba(0,0,0,0.0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.55)');
  c.fillStyle = grad; c.fillRect(0, 0, w, h);
  const cardW = Math.min(400, w * 0.9), cardH = 280, x = (w - cardW) / 2, y = (h - cardH) / 2;
  c.fillStyle = 'rgba(0,0,0,0.35)'; c.beginPath(); roundRect(c, x + 6, y + 10, cardW, cardH, 16); c.fill();
  c.fillStyle = '#ffffff'; c.beginPath(); roundRect(c, x, y, cardW, cardH, 16); c.fill();
  c.strokeStyle = '#cfe2ff'; c.lineWidth = 2; c.stroke();
  const imgSize = 100, imgX = x + (cardW - imgSize) / 2, imgY = y + 16;
  if (badgeImg.complete && badgeImg.naturalWidth > 0) c.drawImage(badgeImg, imgX, imgY, imgSize, imgSize);
  c.textAlign = 'center';
  c.fillStyle = '#0f2542'; c.font = '900 24px system-ui,-apple-system,Segoe UI,Roboto,Arial';
  c.fillText('PUNTUACIÓN', x + cardW / 2, imgY + imgSize + 34);
  c.fillStyle = '#2e7de9'; c.font = '900 66px system-ui,-apple-system,Segoe UI,Roboto,Arial';
  c.fillText(points, x + cardW / 2, y + cardH - 36);
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Initialize the game engine on a canvas element.
 * @param {HTMLCanvasElement} canvasEl - The canvas to render on (must be 480×720)
 * @param {Object} cb - Callbacks: { onScore(pts), onTimer(str), onGameOver(reason, pts), onGameStart(), onStateChange(state) }
 */
export async function initGame(canvasEl, cb = {}) {
  canvas = canvasEl;
  canvas.width = W;
  canvas.height = H;
  ctx = canvas.getContext('2d');
  callbacks = cb;

  // Lazy-load base64 fallbacks
  try {
    const mod = await import('./embedSprites');
    EMBED = mod.EMBED || {};
  } catch (e) {
    console.warn('[DoggyJump] embedSprites not loaded, using assets only');
  }

  // Load sprites
  playerIdle = loadWithFallback(ASSET.idle, EMBED.idle, 'idle');
  playerJump = loadWithFallback(ASSET.jump, EMBED.jump, 'jump');
  playerFall = loadWithFallback(ASSET.fall, EMBED.fall, 'fall');
  bgImg      = loadWithFallback(ASSET.bg, EMBED.bg, 'bg');
  platformImg = loadWithFallback(ASSET.platform, EMBED.platform, 'platform');
  badgeImg   = loadWithFallback(ASSET.badge, EMBED.badge, 'badge');

  // Pre-fetch SFX as ArrayBuffers (decoded later on user gesture)
  prefetchAudio('start', ASSET.sfx.start, 0.7);
  prefetchAudio('jump',  ASSET.sfx.jump,  0.75);
  prefetchAudio('coin',  ASSET.sfx.coin,  0.8);
  prefetchAudio('fall',  ASSET.sfx.fall,  0.8);
  prefetchAudio('over',  ASSET.sfx.over,  0.8);
  prefetchAudio('tick',  ASSET.sfx.tick,  0.75);

  // SFX thin wrappers — play via Web Audio API
  SFX = {
    start: () => playSfx('start'),
    jump:  () => playSfx('jump'),
    coin:  () => playSfx('coin'),
    fall:  () => playSfx('fall'),
    over:  () => playSfx('over'),
    tick:  () => playSfx('tick'),
  };

  // bgMusic created lazily in tryPlayMusic() on user gesture (Android compat)

  // Keyboard listeners (Arrow keys + WASD)
  keydownHandler = (e) => {
    const k = e.key;
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', ' '].includes(k)) e.preventDefault();
    keys.left  = keys.left  || k === 'ArrowLeft'  || k === 'a' || k === 'A';
    keys.right = keys.right || k === 'ArrowRight' || k === 'd' || k === 'D';
    if (k === 'ArrowUp' || k === ' ' || k === 'w' || k === 'W') { keys.jump = true; keys.jumpPressed = true; }
    if (k.toLowerCase() === 'r') keys.restart = true;
  };
  keyupHandler = (e) => {
    const k = e.key;
    if (k === 'ArrowLeft'  || k === 'a' || k === 'A') keys.left = false;
    if (k === 'ArrowRight' || k === 'd' || k === 'D') keys.right = false;
    if (k === 'ArrowUp' || k === ' ' || k === 'w' || k === 'W') keys.jump = false;
  };
  window.addEventListener('keydown', keydownHandler);
  window.addEventListener('keyup', keyupHandler);

  // First-interaction audio unlock
  ['click', 'keydown', 'touchstart'].forEach(ev => {
    const handler = function once() {
      tryPlayMusic();
      ensureAudio();
      window.removeEventListener(ev, once, { capture: false });
    };
    firstInteractionHandlers.push({ ev, handler });
    window.addEventListener(ev, handler, { once: true, capture: false });
  });

  // AudioContext unlock on pointer events
  ['click', 'pointerdown', 'touchstart', 'keydown'].forEach(evt => {
    window.addEventListener(evt, () => { ensureAudio(); }, { passive: true, once: true });
  });

  // Reset & start loop
  game.reset();
  lastTs = 0;
  animFrameId = requestAnimationFrame(loop);
}

/**
 * Destroy the game engine — cleanup all listeners, cancel animation, stop audio.
 */
export function destroyGame() {
  // Cancel animation
  if (animFrameId) {
    cancelAnimationFrame(animFrameId);
    animFrameId = null;
  }

  // Remove keyboard listeners
  if (keydownHandler) window.removeEventListener('keydown', keydownHandler);
  if (keyupHandler) window.removeEventListener('keyup', keyupHandler);
  keydownHandler = null;
  keyupHandler = null;

  // Remove first-interaction handlers
  firstInteractionHandlers.forEach(({ ev, handler }) => {
    window.removeEventListener(ev, handler, { capture: false });
  });
  firstInteractionHandlers = [];

  // Stop music
  if (bgMusic) {
    bgMusic.pause();
    bgMusic.currentTime = 0;
    bgMusic = null;
  }
  musicEnabled = true;

  // Close audio context
  if (audioCtx) {
    try { audioCtx.close(); } catch (e) { /* ignore */ }
    audioCtx = null;
    audioUnlocked = false;
  }

  // Reset state
  SFX = {};
  audioBufferCache = {};
  sfxVolumes = {};
  callbacks = {};
  canvas = null;
  ctx = null;
}

/**
 * Start the game (called when user presses JUGAR).
 */
export function startGame() {
  game.start();
}

/**
 * Reset the game (called when user presses REINTENTAR).
 */
export function restartGame() {
  game.reset();
}

/**
 * Toggle background music mute.
 * @returns {boolean} true if music is now playing (unmuted)
 */
export function toggleMusic() {
  if (!bgMusic) return false;
  if (bgMusic.muted) {
    bgMusic.muted = false;
    tryPlayMusic();
  } else {
    bgMusic.muted = true;
  }
  return !bgMusic.muted;
}

/**
 * Download a styled score card screenshot.
 * @param {number|string} points - The final score
 */
export function downloadScreenshot(points) {
  if (!canvas) return;
  const fileName = `doggyjump_score_${points}_${Date.now()}.png`;
  const tmp = document.createElement('canvas');
  tmp.width = canvas.width;
  tmp.height = canvas.height;
  const ax = tmp.getContext('2d');
  ax.drawImage(canvas, 0, 0);
  drawScoreCard(ax, tmp.width, tmp.height, points);
  if (tmp.toBlob) {
    tmp.toBlob(b => {
      const url = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  } else {
    const dataUrl = tmp.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
  }
}

/**
 * Open Twitter share intent with score.
 * @param {number|string} score - The final score
 */
export function shareOnTwitter(score) {
  const text = `🐕 ¡Hice ${score} puntos en Doggy Jump! ¿Puedes superarme?\n\n$DOGGY en Solana 🔥`;
  const url = 'https://eldoggy.com';
  window.open(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    '_blank', 'width=600,height=400'
  );
}
